import type { ContentResponse, SiteFlowError, SiteFlowErrorCode } from './types.js';

export function createError(
  code: SiteFlowErrorCode,
  message: string,
  extra?: { status?: number; key?: string },
): SiteFlowError {
  const error = new Error(message) as SiteFlowError;
  error.name = 'SiteFlowError';
  error.code = code;
  if (extra?.status !== undefined) error.status = extra.status;
  if (extra?.key !== undefined) error.key = extra.key;
  return error;
}

export interface FetchContentOptions {
  apiBaseUrl: string;
  siteKey: string;
  /** Normalised page slug, always with a leading slash. */
  slug: string;
  locale?: string | undefined;
  timeoutMs: number;
  /** Validator from the previous response, if any. */
  etag?: string | null;
  /** Edit token from the editor; sent as a bearer token to ask for drafts instead of published content. */
  preview?: string | null | undefined;
}

export interface FetchContentResult {
  /** 200 with a body, or 304 when the cached revision is still current. */
  status: 200 | 304;
  data?: ContentResponse;
  etag?: string | null;
}

export function buildContentUrl(options: FetchContentOptions): string {
  const base = options.apiBaseUrl.replace(/\/+$/, '');
  const site = encodeURIComponent(options.siteKey);
  // The edit token is never put in the URL (URLs reach access logs and proxies);
  // it travels in the Authorization header instead.
  const query = options.locale ? `?locale=${encodeURIComponent(options.locale)}` : '';
  // The slug is substituted verbatim: it is already normalised to a leading
  // slash and the public route is defined as `/pages{slug}/content`.
  return `${base}/api/v1/public/sites/${site}/pages${options.slug}/content${query}`;
}

/**
 * Fetches published content for one page.
 *
 * Requests are deliberately anonymous — `credentials: 'omit'` means no cookies
 * travel to the CMS from a customer's site, so the endpoint can stay cacheable
 * and cannot be used to correlate visitors.
 */
export async function fetchContent(options: FetchContentOptions): Promise<FetchContentResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), options.timeoutMs);

  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.etag) headers['if-none-match'] = options.etag;
  if (options.preview) headers['authorization'] = `Bearer ${options.preview}`;

  let response: Response;
  try {
    response = await fetch(buildContentUrl(options), {
      method: 'GET',
      credentials: 'omit',
      mode: 'cors',
      redirect: 'follow',
      headers,
      signal: controller.signal,
    });
  } catch (cause) {
    throw controller.signal.aborted
      ? createError('timeout', `Content request timed out after ${options.timeoutMs}ms`)
      : createError('network', `Content request failed: ${describe(cause)}`);
  } finally {
    clearTimeout(timer);
  }

  if (response.status === 304) return { status: 304, etag: options.etag ?? null };

  if (!response.ok) {
    throw createError('http', `Content request returned ${response.status}`, {
      status: response.status,
    });
  }

  let data: unknown;
  try {
    data = await response.json();
  } catch (cause) {
    throw createError('parse', `Content response was not valid JSON: ${describe(cause)}`);
  }

  if (!data || typeof data !== 'object' || typeof (data as ContentResponse).entries !== 'object') {
    throw createError('parse', 'Content response did not contain an entries object');
  }

  return {
    status: 200,
    data: data as ContentResponse,
    etag: response.headers?.get('etag') ?? null,
  };
}

function describe(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}
